import { Container } from '@/ui/design-system/Container'
import { Button } from '@/ui/components'
import { getOptimizedImageUrl, MEDIA_CONFIG } from '@/shared/utils/media'
import { cn } from '@/shared/utils'
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion'
import { useRef, useState } from 'react'
import {
  CampspotterWeatherLandingAnimation,
  GearManagementAnimation,
  PhotoSharingAnimation,
} from '@/ui/components'
import { ProBadge } from '../ProBadge'
import { ExternalLink } from '@/ui/icons'


interface FeatureSectionProps {
  title: string
  description: string
  benefits: string[]
  image?: string // Image or GIF URL
  imageAlt?: string
  video?: string // Optional MP4 video URL (overrides image if provided)
  poster?: string // Video poster image (fallback while loading)
  layout: 'left' | 'right'
  ctaLabel?: string
  ctaHref?: string
  renderMedia?: () => React.ReactNode
  pro?: boolean
  partner?: { name: string; href?: string; label?: string; logoSrc?: string }
}

export function FeatureMedia({
  image,
  video,
  poster,
  imageAlt,
  renderMedia
}: {
  image?: string
  video?: string
  poster?: string
  imageAlt?: string
  renderMedia?: () => React.ReactNode
}) {
  const prefersReducedMotion = useReducedMotion()

  if (renderMedia) {
    return (
      <div className="w-full">
        {renderMedia()}
      </div>
    )
  }

  // Determine if it's a GIF
  const isGif = Boolean(image && image.toLowerCase().endsWith('.gif'))

  // If video is provided, use video element
  if (video) {
    return (
      <video
        src={video}
        poster={poster || image}
        autoPlay={!prefersReducedMotion}
        loop={!prefersReducedMotion}
        muted
        playsInline
        preload="auto"
        className="w-full h-auto"
        aria-label={imageAlt}
      />
    )
  }

  if (!image) {
    return null
  }

  // If it's a GIF, use as-is (no optimization to preserve animation)
  if (isGif) {
    return (
      <img
        src={image}
        alt={imageAlt}
        className="w-full h-auto"
        loading="lazy"
      />
    )
  }

  // Regular image with optimization
  return (
    <img
      src={getOptimizedImageUrl(image, 1200, 800, 90)}
      alt={imageAlt}
      className="w-full h-auto"
      loading="lazy"
    />
  )
}

// ─── Meal Planning Interactive Demo ──────────────────────────────────────────

const _MEAL_CREW = [
  { name: 'Alex',   initials: 'AL', avatarClass: 'bg-adventure-100 text-adventure-700 dark:bg-adventure-900/40 dark:text-adventure-300', tags: [] as string[] },
  { name: 'Priya',  initials: 'PR', avatarClass: 'bg-earth-200 text-earth-700 dark:bg-earth-700 dark:text-earth-100', tags: ['vegetarian'] },
  { name: 'Sam',    initials: 'SM', avatarClass: 'bg-adventure-200 text-adventure-800 dark:bg-adventure-800/60 dark:text-adventure-100', tags: ['nut-allergy'] },
  { name: 'Jordan', initials: 'JO', avatarClass: 'bg-earth-300 text-earth-800 dark:bg-earth-600 dark:text-earth-50', tags: [] as string[] },
]

const _MEAL_LIST = [
  { name: 'Campfire Chili',      slot: 'Fri · Dinner',   vegetarian: true,  nuts: false },
  { name: 'Trailhead Tacos',     slot: 'Sat · Lunch',    vegetarian: false, nuts: false },
  { name: 'Dutch Oven Cobbler',  slot: 'Sat · Dessert',  vegetarian: true,  nuts: true },
  { name: 'Cast Iron Breakfast', slot: 'Sun · Morning',  vegetarian: false, nuts: false },
]

function MealPlanningInteractiveDemo() {
  const reducedMotion = useReducedMotion() ?? false
  const [cooks, setCooks] = useState([0, 1, 2, 3])

  const nextCook = (mealIdx: number) =>
    setCooks((prev) => prev.map((c, i) => (i === mealIdx ? (c + 1) % _MEAL_CREW.length : c)))

  const getConflict = (mealIdx: number): string | null => {
    const meal = _MEAL_LIST[mealIdx]
    const cook = _MEAL_CREW[cooks[mealIdx]]
    if (cook.tags.includes('vegetarian') && !meal.vegetarian) return `${cook.name} is vegetarian`
    if (cook.tags.includes('nut-allergy') && meal.nuts) return `${cook.name} has a nut allergy`
    return null
  }

  const conflictCount = _MEAL_LIST.filter((_, i) => getConflict(i) !== null).length

  return (
    <div className="overflow-hidden rounded-2xl border border-earth-200 bg-white shadow-xl dark:border-earth-700 dark:bg-earth-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-earth-100 px-5 py-3.5 dark:border-earth-800">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-earth-500 dark:text-earth-400">Meal Planner</p>
          <p className="text-sm font-semibold text-earth-900 dark:text-earth-50">Mojave Spring Run</p>
        </div>
        <AnimatePresence mode="wait">
          {conflictCount > 0 ? (
            <motion.span
              key="conflict"
              initial={reducedMotion ? false : { opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="rounded-full bg-warning/10 px-3 py-1 text-[11px] font-medium text-warning"
            >
              {conflictCount} conflict{conflictCount > 1 ? 's' : ''}
            </motion.span>
          ) : (
            <motion.span
              key="ok"
              initial={reducedMotion ? false : { opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="rounded-full bg-adventure-50 px-3 py-1 text-[11px] font-medium text-adventure-700 dark:bg-adventure-900/30 dark:text-adventure-300"
            >
              All clear
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Crew strip */}
      <div className="flex items-center gap-3 border-b border-earth-100 px-5 py-3 dark:border-earth-800">
        <span className="text-[10px] uppercase tracking-[0.15em] text-earth-400 dark:text-earth-500">Crew</span>
        <div className="flex items-center gap-2">
          {_MEAL_CREW.map((member) => (
            <div key={member.name} className="flex items-center gap-1.5">
              <div className={cn('flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold', member.avatarClass)}>
                {member.initials}
              </div>
              {member.tags.length > 0 && (
                <span className="rounded-full bg-earth-100 px-1.5 py-0.5 text-[9px] text-earth-500 dark:bg-earth-800 dark:text-earth-400">
                  {member.tags[0].replace('-', ' ')}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Meal rows */}
      <div className="space-y-2 px-5 py-4">
        {_MEAL_LIST.map((meal, i) => {
          const cook = _MEAL_CREW[cooks[i]]
          const conflict = getConflict(i)
          return (
            <motion.div
              key={meal.name}
              layout={!reducedMotion}
              className={cn(
                'flex items-center gap-3 rounded-xl border px-3.5 py-3 transition-colors duration-300',
                conflict ? 'border-warning/30 bg-warning/5' : 'border-earth-100 bg-earth-50 dark:border-earth-800 dark:bg-earth-800/60',
              )}
            >
              {/* Name + slot */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight text-earth-900 dark:text-earth-50">{meal.name}</p>
                <p className="text-[11px] text-earth-500 dark:text-earth-400">{meal.slot}</p>
              </div>

              {/* Dietary tags */}
              <div className="flex items-center gap-1">
                {meal.vegetarian && (
                  <span className="rounded-full bg-adventure-50 px-1.5 py-0.5 text-[9px] font-medium text-adventure-700 dark:bg-adventure-900/30 dark:text-adventure-300">veg</span>
                )}
                {meal.nuts && (
                  <span className="rounded-full bg-warning/10 px-1.5 py-0.5 text-[9px] font-medium text-warning">nuts</span>
                )}
              </div>

              {/* Cook assignment */}
              <button
                onClick={() => nextCook(i)}
                aria-label={`Reassign cook for ${meal.name}`}
                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 shadow-sm transition-colors hover:bg-earth-100 active:scale-95 dark:bg-earth-900 dark:hover:bg-earth-700"
              >
                <div className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold', cook.avatarClass)}>
                  {cook.initials}
                </div>
                <span className="text-[11px] text-earth-700 dark:text-earth-200">{cook.name}</span>
              </button>

              {/* Conflict badge */}
              <AnimatePresence>
                {conflict && (
                  <motion.span
                    initial={reducedMotion ? false : { opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    title={conflict}
                    className="shrink-0 rounded-full bg-warning/15 px-2 py-0.5 text-[9px] font-medium text-warning"
                  >
                    Conflict
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}
      </div>

      {/* Footer hint */}
      <div className="border-t border-earth-100 bg-earth-50 px-5 py-3 dark:border-earth-800 dark:bg-earth-950">
        <p className="text-[10px] text-earth-400 dark:text-earth-500">Tap a cook to reassign. Dietary conflicts surface automatically.</p>
      </div>
    </div>
  )
}

function CrewCoordinationDemo() {
  const [tab, setTab] = useState<'meals' | 'gear'>('meals')

  return (
    <div className="flex flex-col gap-3">
      <div className="inline-flex w-fit gap-1 rounded-lg bg-earth-100 p-1 dark:bg-earth-800">
        {(['meals', 'gear'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className={cn(
              'rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors',
              tab === t
                ? 'bg-white text-earth-900 shadow-sm dark:bg-earth-900 dark:text-earth-50'
                : 'text-earth-500 hover:text-earth-700 dark:hover:text-earth-200'
            )}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'meals' ? <MealPlanningInteractiveDemo /> : <GearManagementAnimation />}
    </div>
  )
}

export function FeatureSection({
  title,
  description,
  benefits,
  image,
  imageAlt,
  video,
  poster,
  layout,
  ctaLabel,
  ctaHref,
  renderMedia,
  pro,
  partner,
  index
}: FeatureSectionProps & { index?: number }) {
  const isLeftLayout = layout === 'left'
  const isEvenIndex = index !== undefined && index % 2 === 1 // odd indices (1, 3, 5) get earth tone
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 })
  const reducedMotion = useReducedMotion()
  const inView = reducedMotion || isInView

  const fadeInUp = {
    hidden: reducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.6, -0.05, 0.01, 0.99] as const }
    }
  }

  const fadeInLeft = {
    hidden: reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }
    }
  }

  const fadeInRight = {
    hidden: reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }
    }
  }

  return (
    <section ref={sectionRef} className={`py-20 lg:py-40 ${isEvenIndex ? 'bg-earth-50' : 'bg-white'}`}>
      <Container size="app">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Media Side */}
          <motion.div
            className={cn(
              'relative',
              !isLeftLayout && 'lg:order-2'
            )}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={isLeftLayout ? fadeInLeft : fadeInRight}
          >
            {renderMedia ? (
              <FeatureMedia renderMedia={renderMedia} />
            ) : (
              <div className="relative overflow-hidden rounded-2xl border border-earth-200 shadow-xl dark:border-earth-700">
                <FeatureMedia image={image} video={video} poster={poster} imageAlt={imageAlt} />
              </div>
            )}
          </motion.div>

          {/* Content Side */}
          <motion.div
            className={cn(
              'flex flex-col gap-6',
              !isLeftLayout && 'lg:order-1'
            )}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={isLeftLayout ? fadeInRight : fadeInLeft}
          >
            <div className="flex flex-col gap-3">
              {(pro || partner) && (
                <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-2">
                  {pro && <ProBadge />}
                  {partner && (
                    <a
                      href={partner.href}
                      target={partner.href ? '_blank' : undefined}
                      rel={partner.href ? 'noopener noreferrer' : undefined}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border border-info/30 bg-info/10 px-3 py-1 text-xs font-medium text-info transition-colors',
                        partner.href ? 'hover:bg-info/20' : 'cursor-default'
                      )}
                    >
                      {partner.logoSrc ? (
                        <img src={partner.logoSrc} alt="" className="h-3.5 w-3.5 rounded-sm" />
                      ) : (
                        <span className="h-1.5 w-1.5 rounded-full bg-info" />
                      )}
                      {partner.label ?? `In partnership with ${partner.name}`}
                      {partner.href && <ExternalLink className="h-3 w-3" />}
                    </a>
                  )}
                </motion.div>
              )}
              <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
                {title}
              </h2>
            </div>

            <p className="text-lg text-muted-foreground leading-relaxed">
              {description}
            </p>

            <ul className="space-y-3">
              {benefits.map((benefit, idx) => (
                <motion.li
                  key={idx}
                  className="flex items-start gap-3"
                  initial={reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ duration: 0.4, delay: 0.3 + (idx * 0.1), ease: [0.6, -0.05, 0.01, 0.99] as const }}
                >
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-adventure-500 flex-shrink-0" />
                  <span className="text-base text-earth-700">{benefit}</span>
                </motion.li>
              ))}
            </ul>

            {ctaLabel && ctaHref && (
              <motion.div
                className="pt-2"
                initial={reducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }}
              >
                <Button asChild variant="outline" size="lg">
                  <a href={ctaHref}>{ctaLabel}</a>
                </Button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </Container>
    </section>
  )
}

export function FeatureSections() {
  const features: Omit<FeatureSectionProps, 'layout'>[] = [
    {
      title: 'Find dispersed campsite ideas',
      description:
        'Surface potential pull-offs near your itinerary using dead-end style detection on OSM roads. Handy when your first choice is busy or you want to scout alternatives along the corridor.',
      benefits: [
        'Candidates near your route or waypoints',
        'Explore backups without starting from scratch',
        'Optional Ranger-style analysis where available',
        'Add promising spots back into the trip when you like them'
      ],
      video: 'https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/dispersed-camp-finder-demo.mp4',
      poster: 'https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/dispersed-camp-finder-poster.jpg',
      imageAlt: 'Itinerary map highlighting campsite discovery near the route',
      ctaLabel: 'Start Planning',
      ctaHref: '/signup',
      pro: true
    },
    {
      title: 'Camp-smart weather, powered by Campspotter',
      description:
        'We partner with Campspotter to score every stop on your itinerary for how it will actually feel at camp: overnight lows, wind, and rain windows, not a generic city forecast.',
      benefits: [
        'Per-stop camp scores tied to your real trip dates',
        'The conditions that matter outdoors: overnight lows, wind, precip timing',
        'Free plans get reliable basics; Pro unlocks Campspotter scoring',
        'Campspotter is clearly credited wherever their data appears'
      ],
      renderMedia: () => <CampspotterWeatherLandingAnimation />,
      image: MEDIA_CONFIG.mockups.tripDetail,
      imageAlt: 'Trip overview with Campspotter weather assessments for camps and meetups',
      ctaLabel: 'Start Planning',
      ctaHref: '/signup',
      pro: true,
      partner: { name: 'Campspotter', logoSrc: '/campspotter.png' }
    },
    {
      title: 'Sort meals and gear as one crew',
      description: 'Meals and gear are the same problem: who\'s bringing what, and does it work for everyone? Plan both in one shared place so assignments update live, dietary conflicts surface instantly, and nothing gets forgotten or doubled up at camp.',
      benefits: [
        'Assign cooks and gear owners. Tap to reassign in seconds',
        'Dietary flags travel with each crew member; conflicts surface instantly',
        'Shared checklists show what\'s covered and what\'s still needed',
        'Everyone sees the same plan from their phone'
      ],
      renderMedia: () => <CrewCoordinationDemo />,
      imageAlt: 'Interactive crew planner toggling between meal assignments and a shared gear checklist',
      ctaLabel: 'Start Planning',
      ctaHref: '/signup'
    },
    {
      title: 'Relive the trip together',
      description:
        'Upload photos to a shared trip gallery so the whole crew can collect the trip from every rig. Like and comment on each other\'s shots, and pick the best one as the trip cover.',
      benefits: [
        'Shared gallery everyone on the trip can add to',
        'Likes and comments on each photo',
        'Set a favorite as the trip cover',
        'Memories stay organized with the trip, not your camera roll'
      ],
      renderMedia: () => <PhotoSharingAnimation />,
      image: MEDIA_CONFIG.mockups.photoSharing,
      imageAlt: 'Shared trip photo gallery with likes and comments',
      ctaLabel: 'Start Planning',
      ctaHref: '/signup'
    }
  ]

  return (
    <>
      {features.map((feature, index) => (
        <FeatureSection
          key={index}
          {...feature}
          layout={index % 2 === 0 ? 'left' : 'right'}
          index={index}
        />
      ))}
    </>
  )
}
